// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { buildPortalTheme } from '@hopdrive/sdk-theme';

import {
  AppointmentUsageDay,
  DistanceBucket,
  DriverActivity,
  HourCount,
  KpiTrendDay,
  MoveVolumeDay,
  StatusDistribution,
  WeekdayCount,
} from '../utils/reportingMetrics';
import { ChartColors } from '../utils/reportingChartColors';

// CONSTANTS ---------------------------------------------------------------- //

const CHART_HEIGHT = 260;

/** Match chart text to the site typography (recharts otherwise falls back to the browser default font) */
const chartFontFamily = buildPortalTheme('light').typography.fontFamily;

const axisTickStyle = { fontSize: 12, fontFamily: chartFontFamily };

const legendStyle = { fontSize: 12, fontFamily: chartFontFamily };

/** Styling for recharts' default tooltip */
const tooltipContentStyle = { fontFamily: chartFontFamily, fontSize: 12, borderRadius: 4, border: '1px solid #ccc' };
const tooltipLabelStyle = { fontWeight: 600 };

/** Container styling for the custom tooltip components */
const customTooltipStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #ccc',
  borderRadius: 4,
  padding: '6px 10px',
  fontSize: 12,
  fontFamily: chartFontFamily,
};

// MOVE VOLUME ---------------------------------------------------------------- //

interface MoveVolumeChartProps {
  data: MoveVolumeDay[];
  colors: ChartColors;
}

/** Stacked daily bars of completed/pending/canceled/other moves */
export const MoveVolumeChart = ({ data, colors }: MoveVolumeChartProps) => (
  <ResponsiveContainer width='100%' height={CHART_HEIGHT}>
    <BarChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
      <CartesianGrid strokeDasharray='3 3' vertical={false} />
      <XAxis dataKey='label' tick={axisTickStyle} interval='preserveStartEnd' minTickGap={24} />
      <YAxis allowDecimals={false} tick={axisTickStyle} />
      <Tooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} />
      <Legend wrapperStyle={legendStyle} />
      <Bar dataKey='completed' name='Completed' stackId='volume' fill={colors.completed} />
      <Bar dataKey='pending' name='Pending' stackId='volume' fill={colors.pending} />
      <Bar dataKey='canceled' name='Canceled' stackId='volume' fill={colors.canceled} />
      <Bar dataKey='other' name='In Progress / Other' stackId='volume' fill={colors.neutral} />
    </BarChart>
  </ResponsiveContainer>
);

// STATUS DONUT ---------------------------------------------------------------- //

interface StatusDonutChartProps {
  data: StatusDistribution[];
  colors: ChartColors;
}

/** Donut chart of move status distribution */
export const StatusDonutChart = ({ data, colors }: StatusDonutChartProps) => {
  const colorByKey: Record<StatusDistribution['key'], string> = {
    completed: colors.completed,
    pending: colors.pending,
    canceled: colors.canceled,
    inProgress: colors.neutral,
  };

  const visible = data.filter(d => d.count > 0);

  return (
    <ResponsiveContainer width='100%' height={CHART_HEIGHT}>
      <PieChart>
        <Pie data={visible} dataKey='count' nameKey='label' innerRadius={60} outerRadius={90} paddingAngle={2}>
          {visible.map(entry => (
            <Cell key={entry.key} fill={colorByKey[entry.key]} />
          ))}
        </Pie>
        <Tooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} />
        <Legend wrapperStyle={legendStyle} />
      </PieChart>
    </ResponsiveContainer>
  );
};

// SIMPLE VERTICAL BARS ---------------------------------------------------------------- //

interface CountBarChartProps {
  data: Array<WeekdayCount | HourCount | DistanceBucket>;
  color: string;
  name?: string;
}

/** Generic vertical bar chart for weekday/hour/distance counts */
export const CountBarChart = ({ data, color, name = 'Moves' }: CountBarChartProps) => (
  <ResponsiveContainer width='100%' height={CHART_HEIGHT}>
    <BarChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
      <CartesianGrid strokeDasharray='3 3' vertical={false} />
      <XAxis dataKey='label' tick={axisTickStyle} interval='preserveStartEnd' minTickGap={16} />
      <YAxis allowDecimals={false} tick={axisTickStyle} />
      <Tooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} />
      <Bar dataKey='count' name={name} fill={color} />
    </BarChart>
  </ResponsiveContainer>
);

// APPOINTMENT UTILIZATION ---------------------------------------------------------------- //

interface UtilizationByDayChartProps {
  data: AppointmentUsageDay[];
  colors: ChartColors;
}

type UtilizationDatum = AppointmentUsageDay & { utilizationPercent: number | null };

const UtilizationTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: UtilizationDatum }> }) => {
  if (!active || !payload?.length) return null;
  const day = payload[0].payload;
  return (
    <div style={customTooltipStyle}>
      <div style={{ fontWeight: 600 }}>{day.label}</div>
      <div>{day.capacity > 0 ? `${Math.round((day.utilization || 0) * 100)}% estimated utilization` : 'No estimated capacity'}</div>
      <div>
        {day.scheduled} booked / {day.capacity} estimated slots
      </div>
    </div>
  );
};

/** Daily estimated utilization percentage bars with scheduled/capacity tooltip */
export const UtilizationByDayChart = ({ data, colors }: UtilizationByDayChartProps) => {
  const chartData: UtilizationDatum[] = data.map(day => ({
    ...day,
    utilizationPercent: day.utilization != null ? Math.round(day.utilization * 100) : null,
  }));

  return (
    <ResponsiveContainer width='100%' height={CHART_HEIGHT}>
      <BarChart data={chartData} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
        <CartesianGrid strokeDasharray='3 3' vertical={false} />
        <XAxis dataKey='label' tick={axisTickStyle} interval='preserveStartEnd' minTickGap={24} />
        <YAxis
          allowDecimals={false}
          tick={axisTickStyle}
          unit='%'
          domain={[0, 100]}
          ticks={[0, 25, 50, 75, 100]}
        />
        <Tooltip content={<UtilizationTooltip />} />
        <Bar dataKey='utilizationPercent' name='Estimated utilization' fill={colors.assigned} />
      </BarChart>
    </ResponsiveContainer>
  );
};

interface ScheduledVsCapacityChartProps {
  data: AppointmentUsageDay[];
  colors: ChartColors;
}

/** Overlapping daily areas comparing booked moves against estimated capacity */
export const ScheduledVsCapacityChart = ({ data, colors }: ScheduledVsCapacityChartProps) => (
  <ResponsiveContainer width='100%' height={CHART_HEIGHT}>
    <AreaChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
      <CartesianGrid strokeDasharray='3 3' vertical={false} />
      <XAxis dataKey='label' tick={axisTickStyle} interval='preserveStartEnd' minTickGap={24} />
      <YAxis allowDecimals={false} tick={axisTickStyle} />
      <Tooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} />
      <Legend wrapperStyle={legendStyle} />
      <Area
        type='monotone'
        dataKey='capacity'
        name='Estimated capacity'
        stroke={colors.neutral}
        fill={colors.neutral}
        fillOpacity={0.2}
      />
      <Area
        type='monotone'
        dataKey='scheduled'
        name='Booked'
        stroke={colors.completed}
        fill={colors.completed}
        fillOpacity={0.35}
      />
    </AreaChart>
  </ResponsiveContainer>
);

// KPI GAUGE ---------------------------------------------------------------- //

interface KpiGaugeProps {
  /** 0-1 fraction, or null when unavailable */
  value: number | null;
  color: string;
  trackColor?: string;
  size?: number;
}

/** Simple circular progress gauge built from a donut PieChart */
export const KpiGauge = ({ value, color, trackColor = '#eeeeee', size = 140 }: KpiGaugeProps) => {
  const fraction = value != null && Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0;
  const data = [
    { name: 'value', value: fraction },
    { name: 'rest', value: 1 - fraction },
  ];

  return (
    <ResponsiveContainer width='100%' height={size}>
      <PieChart>
        <Pie
          data={data}
          dataKey='value'
          startAngle={90}
          endAngle={-270}
          innerRadius='70%'
          outerRadius='95%'
          isAnimationActive={false}
        >
          <Cell fill={color} />
          <Cell fill={trackColor} />
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  );
};

// KPI TREND ---------------------------------------------------------------- //

interface KpiTrendChartProps {
  data: KpiTrendDay[];
  colors: ChartColors;
}

type KpiTrendDatum = {
  label: string;
  assigned: number | null;
  completed: number | null;
  validated: number | null;
  totalCount: number;
  assignedCount: number;
  completedCount: number;
  auditedCount: number;
  validatedCount: number;
};

/** Tooltip with raw counts so 0%/100% swings on low-volume days have context */
const KpiTrendTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: KpiTrendDatum }> }) => {
  if (!active || !payload?.length) return null;
  const day = payload[0].payload;
  return (
    <div style={customTooltipStyle}>
      <div style={{ fontWeight: 600 }}>
        {day.label} ({day.totalCount} {day.totalCount === 1 ? 'move' : 'moves'})
      </div>
      <div>{day.assigned != null ? `Assigned: ${day.assigned}% (${day.assignedCount} of ${day.totalCount})` : 'Assigned: no moves'}</div>
      <div>{day.completed != null ? `Completed: ${day.completed}% (${day.completedCount} of ${day.totalCount})` : 'Completed: no moves'}</div>
      <div>
        {day.validated != null
          ? `Validated: ${day.validated}% (${day.validatedCount} of ${day.auditedCount} audited)`
          : 'Validated: no audited completions'}
      </div>
    </div>
  );
};

/** Daily assigned/completed/validated percentage lines */
export const KpiTrendChart = ({ data, colors }: KpiTrendChartProps) => {
  const chartData: KpiTrendDatum[] = data.map(day => ({
    label: day.label,
    assigned: day.assignedPercent != null ? Math.round(day.assignedPercent * 100) : null,
    completed: day.completedPercent != null ? Math.round(day.completedPercent * 100) : null,
    validated: day.validatedPercent != null ? Math.round(day.validatedPercent * 100) : null,
    totalCount: day.totalCount,
    assignedCount: day.assignedCount,
    completedCount: day.completedCount,
    auditedCount: day.auditedCount,
    validatedCount: day.validatedCount,
  }));

  return (
    <ResponsiveContainer width='100%' height={CHART_HEIGHT}>
      <LineChart data={chartData} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
        <CartesianGrid strokeDasharray='3 3' vertical={false} />
        <XAxis dataKey='label' tick={axisTickStyle} interval='preserveStartEnd' minTickGap={24} />
        <YAxis domain={[0, 100]} tick={axisTickStyle} unit='%' />
        <Tooltip content={<KpiTrendTooltip />} />
        <Legend wrapperStyle={legendStyle} />
        <Line type='monotone' dataKey='assigned' name='Assigned' stroke={colors.assigned} dot={false} connectNulls />
        <Line type='monotone' dataKey='completed' name='Completed' stroke={colors.completed} dot={false} connectNulls />
        <Line type='monotone' dataKey='validated' name='Validated' stroke={colors.validated} dot={false} connectNulls />
      </LineChart>
    </ResponsiveContainer>
  );
};

// HORIZONTAL BARS ---------------------------------------------------------------- //

interface HorizontalCountBarChartProps {
  data: DriverActivity[];
  color: string;
  name?: string;
}

/** Horizontal bar chart, used for top-driver activity */
export const HorizontalCountBarChart = ({ data, color, name = 'Moves' }: HorizontalCountBarChartProps) => (
  <ResponsiveContainer width='100%' height={CHART_HEIGHT}>
    <BarChart data={data} layout='vertical' margin={{ top: 4, right: 16, left: 24, bottom: 0 }}>
      <CartesianGrid strokeDasharray='3 3' horizontal={false} />
      <XAxis type='number' allowDecimals={false} tick={axisTickStyle} />
      <YAxis type='category' dataKey='label' width={110} tick={axisTickStyle} />
      <Tooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} />
      <Bar dataKey='count' name={name} fill={color} />
    </BarChart>
  </ResponsiveContainer>
);
